import { useEffect, useState, useRef, memo } from 'react';
import { useReactiveVar } from '@apollo/client';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material';
import theme from 'src/style-system/theme';

import {
  isCartOpen,
  removeCartCount,
  updateCart,
  isModalOpen,
  usePersistentCartItems,
} from 'store/cartItems';
import { usePersistentplanAndFreq } from 'store/planAndFreq';
import { usePersistentPromoCodeDetails } from 'store/promoCode';

import SelectedDishDisplay from '@components/SelectedDishDisplay';
import { getCategorizedProductsType } from '@utils/types';
import CuminButton from '@components/Button';
import useClickOutside from '@utils/useClickOutside';
import SuggestedSides from '@components/SuggestedSides';
import Accordion from '@components/Accordian';
import { addBestSellerProduct } from '@utils/helpers';
import { getDish } from '@containers/CombinedOrderStepThreeAndFour/Review';
import { productFlatListVar } from 'store/productsFlatList';

import { useStyles } from './useStyles';

interface MealDrawerProps {
  categories: getCategorizedProductsType[];
  mode?: string;
  type?: string;
  countryCode?: string;
  mealCounts: number;
  onCheckout: any;
  totalMealCounts?: number;
  getCtaText: string | React.ReactNode;
  isDisabled: boolean;
  //totalMealCounts?: number;
  elevarAddToCartEvent?: any;
  elevarRemoveFromCartEvent?: any;
  elevarViewCartEvent?: any;
  elevarViewItemEvent?: any;
}

const MealDrawer = ({
  categories,
  mode,
  type,
  countryCode,
  mealCounts,
  onCheckout,
  //totalMealCounts = 0,
  getCtaText,
  isDisabled,
  elevarAddToCartEvent,
  elevarRemoveFromCartEvent,
  elevarViewCartEvent,
}: MealDrawerProps) => {
  const [sideCounts, setSideCounts] = useState<number>(0);
  const [mealDraweropen, setMealDrawerOpen] = useState<boolean>(false);

  const isCartBoxOpen = useReactiveVar(isCartOpen);
  const isChildModalOpen = useReactiveVar(isModalOpen);
  const planAndFreq = usePersistentplanAndFreq();
  const promoCodeDetails = usePersistentPromoCodeDetails();
  const cartItems = usePersistentCartItems();
  const { productFlatList } = useReactiveVar(productFlatListVar);

  const mealsAccordian = document.getElementById('dt_meals');
  const sidesAccordian = document.getElementById('dt_sides');
  const freeMealsAccordian = document.getElementById('dt_free_meals');

  useEffect(() => {
    if (planAndFreq.selectedPlan && mealCounts === planAndFreq.selectedPlan) {
      setMealDrawerOpen(true);
      const sidesAccordian1 = document.getElementById('dt_sides');
      sidesAccordian1?.ariaExpanded === 'false' && sidesAccordian1?.click();
    }
  }, [mealCounts]);

  const isIfDrawerOpen = ['order', 'subscription'].includes(type as string)
    ? isCartBoxOpen
    : mealDraweropen || isCartBoxOpen;

  const classes = useStyles();

  const ref = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  //const total_items = type === 'order' ? totalMealCounts : 0;

  useEffect(() => {
    if (isIfDrawerOpen && document?.body?.clientWidth > 900) {
      if (!mode && elevarViewCartEvent) {
        // for elevart view cart event
        setTimeout(() => {
          elevarViewCartEvent();
        }, 700);
      }

      mealsAccordian?.scrollIntoView({ block: 'end' });
      sidesAccordian?.ariaExpanded === 'false' && sidesAccordian?.click();
      if (mealCounts < planAndFreq.selectedPlan) {
        mealsAccordian?.ariaExpanded === 'false' && mealsAccordian?.click();
      } else {
        mealsAccordian?.ariaExpanded === 'true' && mealsAccordian?.click();
      }

      // for free meal accordian
      if (mealCounts === 0) {
        freeMealsAccordian?.ariaExpanded === 'false' &&
          freeMealsAccordian?.click();
      } else if (mealCounts > 0) {
        freeMealsAccordian?.ariaExpanded === 'true' &&
          freeMealsAccordian?.click();
      }

      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isIfDrawerOpen]);

  useClickOutside(ref, () => {
    if (['order', 'subscription'].includes(type as string)) {
      isCartOpen(false);
      return;
    }
    if (isChildModalOpen) {
      return;
    }
    isCartOpen && isCartOpen(false);
    setMealDrawerOpen(false);

    if (isIfDrawerOpen) {
      // for GA event
      //@ts-ignore
      window?.dataLayer.push({
        text: 'Meal Drawer',
        event: 'cart_close',
      });
    }
  });

  const clearCart = () => {
    if (!mode && elevarRemoveFromCartEvent) {
      Object.entries(cartItems).map(([productId, quantity]: any) => {
        const product = productFlatList[productId];

        for (let i = 1; i <= +quantity; i++) {
          //for elvart remove from cart event
          elevarRemoveFromCartEvent(product);
        }
      });
    }

    removeCartCount();

    // for GA event
    //@ts-ignore
    window?.dataLayer.push({
      event: 'empty_cart',
    });
  };

  const fillWithBestSellers = () => {
    const productIdQuantityList: any = {};
    removeCartCount();

    const bestSellerCategory = categories?.find(
      ({ category_name }) => category_name === 'Bestsellers Bundle'
    );

    const addedProduct = addBestSellerProduct({
      bestSellerCategory,
      selectedPlan: planAndFreq.selectedPlan,
      count: 0,
      productIdQuantityList,
    });

    updateCart(addedProduct);

    if (!mode && elevarAddToCartEvent) {
      Object.entries(addedProduct).map(([productId, quantity]: any) => {
        const product = productFlatList[productId];

        for (let i = 1; i <= +quantity; i++) {
          //fire elevar add to cart event
          elevarAddToCartEvent(product);
        }
      });
    }

    // for GA event
    //@ts-ignore
    window?.dataLayer.push({
      event: 'cart_bundle_add_to_cart',
    });
  };

  const handleScroll = (e: any) => {
    const scrollToTop = e.target.scrollTop;
    if (headerRef?.current) {
      headerRef.current.style.boxShadow =
        scrollToTop > 10 ? '0px 4px 20px rgba(0, 0, 0, 0.25)' : '';
    }
  };

  return (
    <Drawer
      className={classes.mealDrawer}
      variant="persistent"
      anchor="right"
      open={isIfDrawerOpen}
      sx={{
        [theme.breakpoints.down('sm')]: {
          display: 'none',
        },
      }}
    >
      <Box className={classes.mainConatiner} ref={ref}>
        <Box className={classes.mealDrawerHeaderConatiner} ref={headerRef}>
          <IconButton
            onClick={() => {
              if (['order', 'subscription'].includes(type as string)) {
                isCartOpen(false);
                return;
              }
              isCartOpen && isCartOpen(false);
              setMealDrawerOpen(false);

              // for GA event
              //@ts-ignore
              window?.dataLayer.push({
                text: 'Meal Drawer',
                event: 'cart_close',
              });
            }}
          >
            <ChevronRightIcon
              fontSize="large"
              sx={{ color: 'cinnamon.main' }}
            />
          </IconButton>

          <Typography variant="body1" align="center" color="cinnamon.main">
            My Box
          </Typography>
          <Typography variant="body1" align="center" color="cinnamon.main">
            {/* {mealCounts}/{total_items || planAndFreq.selectedPlan} */}
            {mealCounts}/{planAndFreq.selectedPlan}
          </Typography>
          {mealCounts > 0 ? (
            <Typography
              variant="body2"
              align="center"
              color="cumin.main"
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
              onClick={clearCart}
            >
              Empty Box
            </Typography>
          ) : null}
        </Box>
        <Box
          className={classes.contentWrapper}
          bgcolor="whitePepper.main"
          onScroll={handleScroll}
        >
          {mealCounts > 0 ? (
            <>
              {mode === 'edit' && type !== 'subscription' ? (
                mealCounts < planAndFreq.selectedPlan && (
                  <Typography
                    variant="body2"
                    align="center"
                    color="cinnamon.main"
                    m="0px auto 20px"
                  >
                    You still have space in your box!
                  </Typography>
                )
              ) : (
                <Typography
                  variant="body2"
                  align="center"
                  color="cinnamon.main"
                  m="0px auto 20px"
                >
                  {mealCounts < planAndFreq.selectedPlan
                    ? 'You still have space in your box!'
                    : 'Your box is complete, but you can add more!'}
                </Typography>
              )}
            </>
          ) : null}

          {/*For Free Meals and Gifts*/}
          {promoCodeDetails?.isPromoValid &&
          (promoCodeDetails?.amountType === 'FREE_MEALS' ||
            promoCodeDetails?.amountType === 'FREE_GIFTS') ? (
            <Accordion
              bgcolor={theme.palette.whitePepper.main}
              bordercolor="transparent"
              marginbottom={10}
            >
              <AccordionSummary
                expandIcon={
                  <ArrowDropDownIcon
                    fontSize="large"
                    sx={{ color: 'garamMasala.main' }}
                  />
                }
                id="dt_free_meals"
                aria-controls="panel1a-content"
                sx={{
                  backgroundColor: 'offWhite.main',
                }}
              >
                <Typography
                  variant="body1"
                  align="left"
                  color="garamMasala.main"
                >
                  {promoCodeDetails?.amountType === 'FREE_MEALS'
                    ? 'Free Meals'
                    : 'Welcome Gift'}
                  <Typography component="span" pl="30px">
                    {promoCodeDetails?.products?.length}
                  </Typography>
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ padding: '20px' }}>
                <Box className={classes.dishWrapper}>
                  {promoCodeDetails?.products?.map((product: any) => {
                    return (
                      <Box className={classes.dish} key={product?.id}>
                        {getDish({
                          product,
                          value: product?.quantity,
                          isFreeTextToShow:
                            promoCodeDetails?.amountType === 'FREE_MEALS',
                        })}
                      </Box>
                    );
                  })}
                </Box>
              </AccordionDetails>
            </Accordion>
          ) : null}

          {mealCounts > 0 ? (
            <>
              <Box className={classes.mealWrapper}>
                {/*For Meals*/}
                <Accordion
                  bgcolor={theme.palette.whitePepper.main}
                  bordercolor="transparent"
                  marginbottom={10}
                >
                  <AccordionSummary
                    expandIcon={
                      <ArrowDropDownIcon
                        fontSize="large"
                        sx={{ color: 'garamMasala.main' }}
                      />
                    }
                    id="dt_meals"
                    aria-controls="panel1a-content"
                    sx={{
                      backgroundColor: 'offWhite.main',
                    }}
                  >
                    <Typography
                      variant="body1"
                      align="left"
                      color="garamMasala.main"
                    >
                      Meals
                      <Typography component="span" pl="30px">
                        {mealCounts}
                      </Typography>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: '20px' }}>
                    <SelectedDishDisplay
                      alignItems="flex-start"
                      rowGap={15}
                      isSmallSize
                      isNewDishDisplay
                      sidesIncluded={false}
                      mode={mode}
                    />
                  </AccordionDetails>
                </Accordion>
                {/*For Sides*/}
                <Accordion
                  bgcolor={theme.palette.whitePepper.main}
                  bordercolor="transparent"
                  marginbottom={10}
                >
                  <AccordionSummary
                    expandIcon={
                      <ArrowDropDownIcon
                        fontSize="large"
                        sx={{ color: 'garamMasala.main' }}
                      />
                    }
                    id="dt_sides"
                    sx={{
                      backgroundColor: 'offWhite.main',
                    }}
                    aria-controls="panel1a-content"
                  >
                    <Typography
                      variant="body1"
                      align="left"
                      color="garamMasala.main"
                    >
                      Recommended Sides
                      <Typography component="span" pl="30px">
                        {sideCounts}
                      </Typography>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ padding: '20px' }}>
                    <SuggestedSides
                      categories={categories}
                      setSideCounts={setSideCounts}
                      mode={mode}
                      countryCode={countryCode}
                    />
                  </AccordionDetails>
                </Accordion>
              </Box>
              <Box className={classes.bottomWrapper}>
                <Box className={classes.checkoutBtnWrapper}>
                  <CuminButton
                    color="cinnamon"
                    variant="contained"
                    textColor={isDisabled ? '#ffffff' : 'whitePepper.main'}
                    borderRadius="55"
                    width="425px"
                    height="76"
                    disabled={isDisabled}
                    disabledColor="#E8AD81"
                    onClick={onCheckout}
                  >
                    {getCtaText}
                  </CuminButton>
                </Box>
              </Box>
            </>
          ) : (
            <Box className={classes.contentShow}>
              <Typography variant="body1" align="center" color="cumin.main">
                Your box is empty!
              </Typography>
              <Box className={classes.notSureWrapper}>
                <Typography
                  variant="body1"
                  align="center"
                  color="garamMasala.main"
                  mb={4}
                >
                  Not sure of what to get?
                </Typography>
                <Button
                  variant="outlined"
                  className={classes.seeFullMenuBtnWrapper}
                  onClick={fillWithBestSellers}
                >
                  <Typography
                    variant="button2"
                    align="center"
                    color="cinnamon.main"
                  >
                    Fill Box with Best Sellers
                  </Typography>
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default memo(MealDrawer);
